import { useQueries } from "@tanstack/vue-query";
import type { ICuration, IParagraphEntry } from "~/models/IUserEntry";

export function useFetchParagraphEntries(
  paragraphId: MaybeRef<number | undefined>
) {
  const showcase = useExamShowcase();

  const loggedInTypes: Exclude<IParagraphEntry, ICuration>["type"][] = [
    "mark",
    "underline",
  ];

  const curatedTypes: Extract<IParagraphEntry, ICuration>["type"][] = showcase
    ? ["link_chain"]
    : ["link_chain", "note", "schema"];

  const { loggedIn } = storeToRefs(useUserStore());

  const queryCtx = queries().paragraphs.byId(paragraphId)._ctx;

  const typedQueries = computed(() => [
    ...curatedTypes.map((type) => ({
      ...queryCtx.entriesByParagraph(type),
      enabled: () => Boolean(toValue(paragraphId)),
    })),
    ...(loggedIn.value ? loggedInTypes : []).map((type) => ({
      ...queryCtx.entriesByParagraph(type),
      enabled: () => Boolean(loggedIn.value && toValue(paragraphId)),
    })),
  ]);

  const allQueries = computed(() =>
    toValue(paragraphId) ? typedQueries.value : []
  );

  const combinedQueries = useQueries({
    queries: allQueries,
    combine: (results) => ({
      data: results
        .filter(({ data }) => typeof data !== "undefined")
        .flatMap(({ data }) => data!),
      isLoading: results.some(({ isLoading }) => isLoading),
    }),
  });

  const flattenedEntries = computed(() => combinedQueries.value.data);
  const isLoading = computed(() => combinedQueries.value.isLoading);

  const { amended: data } = useAddLocalEntriesForParagraph(
    flattenedEntries,
    paragraphId
  );

  return { data, isLoading };
}
